/**
 * Custom Kanban Card
 *
 * Beautiful, customizable card component for Kanban boards.
 * Supports different display sizes and configurable fields.
 */

import { useMemo } from 'react';
import type React from 'react';
import { Typography, Avatar, Tooltip, Progress, Space } from 'antd';
import '../grid/blueprint-grid.css';
import {
  CalendarOutlined,
  UserOutlined,
  CheckSquareOutlined,
  WarningFilled,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { ProcessInstance } from '@/types';
import type { CardSettings } from '@api/kanban';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;

interface CustomKanbanCardProps {
  item: ProcessInstance;
  settings: CardSettings;
  onClick: () => void;
  columnColor?: string; // Deprecated: now uses domain color
}

// Priority colors (square dots, Blueprint style)
const priorityColors: Record<string, string> = {
  low: 'var(--status-success)',
  medium: 'var(--status-info)',
  high: 'var(--status-warning)',
  urgent: 'var(--status-error)',
};

// Tag hue map for Blueprint domain colours
const TAG_HUE_MAP: Record<string, string> = {
  fleet:      '#2563a8',
  it:         '#64748b',
  revenue:    '#b45309',
  finance:    '#166534',
  compliance: '#991b1b',
  staff:      '#6d28d9',
};

// Get relative date display
function getDateDisplay(dateStr: string): { text: string; isOverdue: boolean; isSoon: boolean } {
  const date = dayjs(dateStr);
  const now = dayjs();
  const diffDays = date.diff(now, 'day');

  if (diffDays < 0) {
    return { text: `${Math.abs(diffDays)}d overdue`, isOverdue: true, isSoon: false };
  }
  if (diffDays === 0) {
    return { text: 'Today', isOverdue: false, isSoon: true };
  }
  if (diffDays === 1) {
    return { text: 'Tomorrow', isOverdue: false, isSoon: true };
  }
  if (diffDays <= 7) {
    return { text: `${diffDays}d`, isOverdue: false, isSoon: true };
  }
  return { text: date.format('MMM D'), isOverdue: false, isSoon: false };
}

export default function CustomKanbanCard({
  item,
  settings,
  onClick,
}: CustomKanbanCardProps) {
  const {
    showDescription = true,
    showDueDate = true,
    showAssignee = true,
    showTags = true,
    showCategory = true, // Default to true for better visibility
    showSubtasks = true,
    showProgress = false,
    showPriority = false,
    cardSize = 'normal',
    descriptionLines = 2,
  } = settings;

  const template = item.template;
  const title = item.title || template?.title || 'Untitled';
  const description = template?.description || '';
  const tags = item.tags || template?.tags || [];
  const category = template?.category;
  const subtasks = (item as any).subtasks || [];
  const completedSubtasks = subtasks.filter((s: any) => s.completed).length;
  const totalSubtasks = subtasks.length;
  const priority = (item as any).priority || item.priority;

  // Get business domain color (primary color for left border)
  const domain = template?.domain || template?.domains?.[0];
  const domainColor = domain?.color || 'var(--status-info)';

  // Date display
  const dateInfo = useMemo(() => {
    if (!item.dueDate) return null;
    return getDateDisplay(item.dueDate);
  }, [item.dueDate]);

  // Card size classes
  const sizeClass = `card-size-${cardSize}`;

  // Derive tag hue class for a tag based on its name/slug
  const getTagHueClass = (tagName: string): string => {
    const key = tagName.toLowerCase().replace(/[^a-z]/g, '');
    return TAG_HUE_MAP[key] ? `bp-tag-${key}` : '';
  };

  const getTagHueStyle = (tagName: string, fallbackColor?: string): React.CSSProperties => {
    const key = tagName.toLowerCase().replace(/[^a-z]/g, '');
    const hue = TAG_HUE_MAP[key] || fallbackColor;
    if (hue) return { borderLeftColor: hue };
    return {};
  };

  return (
    <div
      className={`custom-kanban-card ${sizeClass} ${dateInfo?.isOverdue ? 'card-overdue' : ''}`}
      onClick={onClick}
      style={{
        borderLeftColor: dateInfo?.isOverdue ? 'var(--status-error, #e5484d)' : domainColor,
      }}
    >
      {/* Priority indicator */}
      {showPriority && priority && (
        <div
          className="card-priority-indicator"
          style={{ backgroundColor: priorityColors[priority] || '#d9d9d9' }}
          title={`Priority: ${priority}`}
        />
      )}

      {/* Business Domain Badge — Blueprint chip */}
      {domain && (
        <div className="card-domain">
          <span
            className="bp-domain-chip"
            style={{ color: domainColor, borderColor: domainColor }}
          >
            {domain.name}
          </span>
        </div>
      )}

      {/* Title */}
      <div className="card-title-row">
        <Text strong className="card-title" ellipsis={{ tooltip: title }}>
          {title}
        </Text>
      </div>

      {/* Description */}
      {showDescription && description && cardSize !== 'compact' && (
        <Paragraph
          className="card-description"
          ellipsis={{ rows: descriptionLines }}
          type="secondary"
        >
          {description}
        </Paragraph>
      )}

      {/* Category — Blueprint chip */}
      {showCategory && category && (
        <div className="card-category">
          <span
            className="bp-tag-chip"
            style={getTagHueStyle(category.name, category.color)}
          >
            {category.name}
          </span>
        </div>
      )}

      {/* Tags — Blueprint chips */}
      {showTags && tags.length > 0 && (
        <div className="card-tags">
          {tags.slice(0, cardSize === 'compact' ? 2 : 4).map((tag) => (
            <span
              key={tag.id}
              className={`bp-tag-chip ${getTagHueClass(tag.name)}`}
              style={getTagHueStyle(tag.name, tag.color)}
            >
              {tag.name}
            </span>
          ))}
          {tags.length > (cardSize === 'compact' ? 2 : 4) && (
            <span className="bp-tag-chip">
              +{tags.length - (cardSize === 'compact' ? 2 : 4)}
            </span>
          )}
        </div>
      )}

      {/* Progress bar */}
      {showProgress && totalSubtasks > 0 && (
        <div className="card-progress">
          <Progress
            percent={Math.round((completedSubtasks / totalSubtasks) * 100)}
            size="small"
            strokeColor={domainColor}
          />
        </div>
      )}

      {/* Footer metadata */}
      <div className="card-footer">
        <Space size={8} wrap>
          {/* Due date */}
          {showDueDate && dateInfo && (
            <Tooltip title={dayjs(item.dueDate).format('MMM D, YYYY')}>
              <span
                className={`card-due-date ${dateInfo.isOverdue ? 'overdue' : ''} ${dateInfo.isSoon ? 'soon' : ''}`}
              >
                {dateInfo.isOverdue ? <WarningFilled /> : dateInfo.isSoon ? <ClockCircleOutlined /> : <CalendarOutlined />}
                <span>{dateInfo.text}</span>
              </span>
            </Tooltip>
          )}

          {/* Subtasks count */}
          {showSubtasks && totalSubtasks > 0 && !showProgress && (
            <span className="card-subtasks">
              <CheckSquareOutlined />
              <span>{completedSubtasks}/{totalSubtasks}</span>
            </span>
          )}
        </Space>

        {/* Assignee */}
        {showAssignee && item.assignedUserId && (
          <Tooltip title={(item as any).assignedUser?.full_name || item.assignedUser?.fullName || 'Assigned'}>
            <Avatar
              size="small"
              src={(item as any).assignedUser?.avatar_url}
              icon={<UserOutlined />}
              className="card-assignee"
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}
